import { useEffect, useMemo, useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import SettingCard from '@/components/settings/SettingCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Copy, Info } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { projectService, type ProjectDto } from '@/services/projectService';
import { useRoleAccess } from '@/hooks/useRoleAccess';

const SUBSCRIPTION_LABELS: Record<number, string> = {
  0: 'Team default',
  1: 'Snapshot (Free)',
  2: 'Archive',
  3: 'Chronicle',
  4: 'Infinity',
  5: 'Enterprise',
};

const ADD_ON_LABELS: Record<number, string> = {
  0: 'No add-on (team default)',
  1: 'Graph Memory Pack – Tier 1',
  2: 'Graph Memory Pack – Tier 2',
  3: 'Graph Memory Pack – Tier 3',
  4: 'Graph Memory Pack – Tier 4',
};

const getSubscriptionLabel = (value?: number | null) => {
  if (value === null || value === undefined) return SUBSCRIPTION_LABELS[0];
  return SUBSCRIPTION_LABELS[value] ?? `Plan ${value}`;
};

const getAddOnLabel = (value?: number | null) => {
  if (value === null || value === undefined) return ADD_ON_LABELS[0];
  return ADD_ON_LABELS[value] ?? `Add-on ${value}`;
};

const Projects = () => {
  const { isAdmin } = useRoleAccess();

  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [savingProjectId, setSavingProjectId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);
  const [editedNames, setEditedNames] = useState<Record<string, string>>({});

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects]
  );

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoadingProjects(true);
        const list = await projectService.list();
        setProjects(list);
        setEditedNames({});
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load projects';
        toast.error('Failed to load projects', { description: message });
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, []);

  const handleCreateProject = async () => {
    if (!isAdmin) {
      toast.error('Permission denied', { description: 'Only admins can create projects' });
      return;
    }

    const name = newProjectName.trim();
    if (!name) {
      toast.error('Project name is required');
      return;
    }

    try {
      setIsSaving(true);
      const created = await projectService.create(name);
      setProjects(prev => [...prev, created]);
      setNewProjectName('');
      toast.success('Project created');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to create project', { description: message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRenameProject = async (projectId: string, name: string) => {
    if (!isAdmin) {
      toast.error('Permission denied', { description: 'Only admins can update projects' });
      return;
    }

    const trimmed = name.trim();
    if (!trimmed) return;

    try {
      setSavingProjectId(projectId);
      const updated = await projectService.update(projectId, trimmed);
      setProjects(prev => prev.map(project => (project.id === projectId ? updated : project)));
      setEditedNames(prev => {
        const { [projectId]: _omit, ...rest } = prev;
        return rest;
      });
      toast.success('Project updated');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to update project', { description: message });
    } finally {
      setSavingProjectId(null);
    }
  };

  const handleSuspendProject = async (projectId: string) => {
    if (!isAdmin) {
      toast.error('Permission denied', { description: 'Only admins can suspend projects' });
      return;
    }

    try {
      const updated = await projectService.suspend(projectId);
      setProjects(prev => prev.map(project => (project.id === projectId ? updated : project)));
      toast.success('Project suspended');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to suspend project', { description: message });
    }
  };

  const handleEnableProject = async (projectId: string) => {
    if (!isAdmin) {
      toast.error('Permission denied', { description: 'Only admins can enable projects' });
      return;
    }

    try {
      const updated = await projectService.enable(projectId);
      setProjects(prev => prev.map(project => (project.id === projectId ? updated : project)));
      toast.success('Project enabled');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to enable project', { description: message });
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!isAdmin) {
      toast.error('Permission denied', { description: 'Only admins can delete projects' });
      return;
    }

    try {
      await projectService.delete(projectId);
      setProjects(prev => prev.filter(project => project.id !== projectId));
      setEditedNames(prev => {
        const { [projectId]: _omit, ...rest } = prev;
        return rest;
      });
      toast.success('Project deleted');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to delete project', { description: message });
    }
  };

  const handleCopyProjectId = async (projectId: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(projectId);
        toast.success('Project ID copied');
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not copy to clipboard';
      toast.error('Failed to copy project ID', { description: message });
    }
  };

  const isViewerOnly = !isAdmin;

  return (
    <PageLayout
      title="Projects"
      description="Create and manage projects for your organization"
    >
      {isViewerOnly && (
        <div className="mb-6 p-4 bg-yellow-500/10 rounded-lg flex gap-3 items-start border border-yellow-500/30">
          <Info className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm">View Only Mode</p>
            <p className="text-xs text-muted-foreground mt-1">Only team administrators can modify projects. You can view project details but changes require admin access.</p>
          </div>
        </div>
      )}

      <SettingCard
        title="Projects"
        description="Create projects, update their details, and control their availability"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <h4 className="text-sm font-medium mb-2">Add a project</h4>
              <div className="flex gap-2 max-w-md">
                <Input
                  placeholder="Project name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="bg-recallio-medium-gray border-recallio-light-gray/50"
                  disabled={!isAdmin}
                />
                <Button
                  onClick={handleCreateProject}
                  disabled={!isAdmin || !newProjectName.trim() || isSaving}
                  className="btn-primary"
                >
                  {isSaving ? 'Adding…' : 'Add'}
                </Button>
              </div>
              {!isAdmin && (
                <p className="text-xs text-muted-foreground mt-2">Only admins can create projects.</p>
              )}

              <div className="mt-6">
                <h4 className="text-sm font-medium mb-2">Projects</h4>
                {loadingProjects ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : sortedProjects.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No projects yet.</p>
                ) : (
                  <div className="space-y-3">
                    {sortedProjects.map((project) => {
                      const draftName = editedNames[project.id] ?? project.name;
                      const trimmedDraft = draftName.trim();
                      const hasNameChanged = trimmedDraft !== project.name.trim();
                      const saveDisabled =
                        !isAdmin || !trimmedDraft || !hasNameChanged || savingProjectId === project.id;
                      const status = (project.status || 'enabled').toLowerCase();
                      const statusBadgeClasses = status === 'suspended'
                        ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                        : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
                      const hasCustomSubscription = (project.subscription ?? 0) !== 1;
                      const hasCustomAddOn = (project.addOn ?? 0) !== 0;
                      const subscriptionLabel = hasCustomSubscription
                        ? getSubscriptionLabel(project.subscription)
                        : null;
                      const addOnLabel = hasCustomAddOn ? getAddOnLabel(project.addOn) : null;
                      const subscriptionBadgeClasses = 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
                      const addOnBadgeClasses = 'bg-purple-500/10 text-purple-400 border border-purple-500/20';

                      return (
                        <div key={project.id} className="rounded-xl border border-border/50 bg-recallio-medium-gray/20 p-6 shadow-sm backdrop-blur-sm space-y-6">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-4">
                            <div className="w-full sm:flex-1 space-y-2">
                              <Label
                                htmlFor={`project-name-${project.id}`}
                                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                              >
                                Project Name
                              </Label>
                              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                                <Input
                                  id={`project-name-${project.id}`}
                                  value={draftName}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEditedNames(prev => {
                                      const next = { ...prev, [project.id]: value };
                                      if (value === project.name) {
                                        const { [project.id]: _omit, ...rest } = next;
                                        return rest;
                                      }
                                      return next;
                                    });
                                  }}
                                  className="bg-recallio-medium-gray border-recallio-light-gray/50 sm:flex-1 sm:w-full min-w-0"
                                  disabled={!isAdmin || savingProjectId === project.id}
                                />
                                <div className="flex flex-wrap gap-2 sm:flex-none">
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={saveDisabled}
                                    onClick={() => handleRenameProject(project.id, draftName)}
                                  >
                                    {savingProjectId === project.id ? 'Saving…' : 'Save Changes'}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={!isAdmin || project.status === 'suspended'}
                                    onClick={() => handleSuspendProject(project.id)}
                                  >
                                    Suspend
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={!isAdmin || project.status === 'enabled'}
                                    onClick={() => handleEnableProject(project.id)}
                                  >
                                    Enable
                                  </Button>
                                </div>
                              </div>
                            </div>
                            <div className="flex justify-end sm:self-start sm:ml-auto">
                              <Badge variant="outline" className={`h-fit px-3 py-1 text-xs font-medium ${statusBadgeClasses}`}>
                                {status === 'suspended' ? 'Suspended' : 'Enabled'}
                              </Badge>
                            </div>
                          </div>

                          <Separator className="bg-border/60" />

                          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {subscriptionLabel && (
                              <div className="space-y-1">
                                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  Subscription
                                </Label>
                                <div>
                                  <Badge variant="outline" className={`px-3 py-1 text-xs font-medium ${subscriptionBadgeClasses}`}>
                                    {subscriptionLabel}
                                  </Badge>
                                </div>
                              </div>
                            )}

                            {addOnLabel && (
                              <div className="space-y-1">
                                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  Add-on
                                </Label>
                                <div>
                                  <Badge variant="outline" className={`px-3 py-1 text-xs font-medium ${addOnBadgeClasses}`}>
                                    {addOnLabel}
                                  </Badge>
                                </div>
                              </div>
                            )}

                            <div className="space-y-2 lg:col-span-3">
                              <Label
                                htmlFor={`project-id-${project.id}`}
                                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                              >
                                Project ID
                              </Label>
                              <div className="flex w-full flex-wrap gap-2 items-center">
                                <Input
                                  id={`project-id-${project.id}`}
                                  value={project.id}
                                  readOnly
                                  className="bg-recallio-medium-gray border-recallio-light-gray/50 flex-1 min-w-0"
                                />
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleCopyProjectId(project.id)}
                                      aria-label="Copy project ID"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Copy project ID</TooltipContent>
                                </Tooltip>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  disabled={!isAdmin}
                                  onClick={() => handleDeleteProject(project.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </SettingCard>
    </PageLayout>
  );
};

export default Projects;

